export interface BadgeDefinition {
  id: string;
  icon: string;
  name: string;
  description: string;
  criteria: {
    type: 'simulations_completed' | 'goals_reached' | 'score_threshold' | 'perfect_score' | 'streak' | 'cpd_points' | 'skill_streak' | 'coaching_completed' | 'readiness_threshold';
    count?: number;
    score?: number;
    days?: number;
    points?: number;
    skill?: string;
  };
}

export const BADGE_DEFINITIONS: BadgeDefinition[] = [
  // Simulation completion badges
  { 
    id: 'first_steps', 
    icon: '🏆', 
    name: 'First Steps', 
    description: 'Complete your first simulation', 
    criteria: { type: 'simulations_completed', count: 1 } 
  },
  { 
    id: 'dedicated_learner', 
    icon: '📖', 
    name: 'Dedicated Learner', 
    description: 'Complete 10 simulations', 
    criteria: { type: 'simulations_completed', count: 10 } 
  },
  { 
    id: 'simulation_master', 
    icon: '🎓', 
    name: 'Simulation Master', 
    description: 'Complete 50 simulations', 
    criteria: { type: 'simulations_completed', count: 50 } 
  },
  { 
    id: 'century_club', 
    icon: '💯', 
    name: 'Century Club', 
    description: 'Complete 100 simulations', 
    criteria: { type: 'simulations_completed', count: 100 } 
  },
  
  // Coaching completion badges
  { 
    id: 'first_coaching', 
    icon: '🎤', 
    name: 'First Coaching', 
    description: 'Complete your first coaching session', 
    criteria: { type: 'coaching_completed', count: 1 } 
  },
  { 
    id: 'coachable', 
    icon: '📝', 
    name: 'Coachable', 
    description: 'Complete 5 coaching sessions', 
    criteria: { type: 'coaching_completed', count: 5 } 
  },
  { 
    id: 'growth_mindset', 
    icon: '🌱', 
    name: 'Growth Mindset', 
    description: 'Complete 10 coaching sessions', 
    criteria: { type: 'coaching_completed', count: 10 } 
  },
  { 
    id: 'coachs_star', 
    icon: '⭐', 
    name: "Coach's Star", 
    description: 'Complete 25 coaching sessions', 
    criteria: { type: 'coaching_completed', count: 25 } 
  },
  
  // Score-based badges
  { 
    id: 'goal_getter', 
    icon: '🎯', 
    name: 'Goal Getter', 
    description: 'Reach goal score 5 times', 
    criteria: { type: 'goals_reached', count: 5 } 
  },
  { 
    id: 'consistent_achiever', 
    icon: '⭐', 
    name: 'Consistent Achiever', 
    description: 'Reach goal score 20 times', 
    criteria: { type: 'goals_reached', count: 20 } 
  },
  { 
    id: 'high_achiever', 
    icon: '🌟', 
    name: 'High Achiever', 
    description: 'Score 90+ on any simulation', 
    criteria: { type: 'score_threshold', score: 90 } 
  },
  { 
    id: 'perfectionist', 
    icon: '💎', 
    name: 'Perfectionist', 
    description: 'Score 100/100', 
    criteria: { type: 'perfect_score' } 
  },
  
  // Coaching readiness badges
  { 
    id: 'highly_prepared', 
    icon: '🎯', 
    name: 'Highly Prepared', 
    description: 'Score 90%+ readiness on any coaching session', 
    criteria: { type: 'readiness_threshold', score: 90 } 
  },
  { 
    id: 'coaching_excellence', 
    icon: '🏅', 
    name: 'Coaching Excellence', 
    description: 'Score 95%+ readiness on any coaching session', 
    criteria: { type: 'readiness_threshold', score: 95 } 
  },
  
  // Streak badges
  { 
    id: 'on_fire', 
    icon: '🔥', 
    name: 'On Fire', 
    description: '7-day learning streak', 
    criteria: { type: 'streak', days: 7 } 
  },
  { 
    id: 'unstoppable', 
    icon: '💪', 
    name: 'Unstoppable', 
    description: '14-day learning streak', 
    criteria: { type: 'streak', days: 14 } 
  },
  { 
    id: 'legend', 
    icon: '👑', 
    name: 'Legend', 
    description: '30-day learning streak', 
    criteria: { type: 'streak', days: 30 } 
  },
  
  // CPD badges
  { 
    id: 'cpd_starter', 
    icon: '📚', 
    name: 'CPD Starter', 
    description: 'Earn 25 CPD points', 
    criteria: { type: 'cpd_points', points: 25 } 
  },
  { 
    id: 'cpd_scholar', 
    icon: '🎖️', 
    name: 'CPD Scholar', 
    description: 'Earn 100 CPD points', 
    criteria: { type: 'cpd_points', points: 100 } 
  },
  { 
    id: 'cpd_expert', 
    icon: '🏅', 
    name: 'CPD Expert', 
    description: 'Earn 250 CPD points', 
    criteria: { type: 'cpd_points', points: 250 } 
  },
];

export function getBadgeById(badgeId: string): BadgeDefinition | undefined {
  return BADGE_DEFINITIONS.find(b => b.id === badgeId);
}
